import type { ProductCompletenessRow, ProductType } from '@utazzo/shared';

/**
 * Shared logic + presentation metadata for the Products dashboard, used by both
 * the by-product view (ProductsDashboardPage) and the by-geography view
 * (ProductsGeographyView). Readiness is grounded in the itinerary pricing path:
 * a product is "ready to quote" when it has a live priced rate and a geography
 * to surface under (plus an image for activities, which carry one into the
 * itinerary). Long-haul unready products (Europe, Japan, South Korea, Sri
 * Lanka - set in the API) are "skipped" in the headline counts, not blocked,
 * until that catalog is fully managed.
 */

export const TEAL = '#113a4a';

export const TYPES: ProductType[] = [
  'activities',
  'transfers',
  'hotels',
  'cruises',
  'visas',
  'insurance',
];

export const TYPE_META: Record<
  ProductType,
  {
    label: string;
    route: string;
    priceLabel: string;
    geoLabel: string;
    showImage: boolean;
    ic: string;
    icBg: string;
    icFg: string;
  }
> = {
  activities: {
    label: 'Activities',
    route: '/activities',
    priceLabel: 'Base price',
    geoLabel: 'City',
    showImage: true,
    ic: 'A',
    icBg: '#e0f2fe',
    icFg: '#0369a1',
  },
  transfers: {
    label: 'Transfers',
    route: '/transfers',
    priceLabel: 'Base rate',
    geoLabel: 'City',
    showImage: false,
    ic: 'T',
    icBg: '#ede9fe',
    icFg: '#6d28d9',
  },
  hotels: {
    label: 'Hotels',
    route: '/hotels',
    priceLabel: 'Priced inventory',
    geoLabel: 'City',
    showImage: false,
    ic: 'H',
    icBg: '#fef3c7',
    icFg: '#b45309',
  },
  cruises: {
    label: 'Cruises',
    route: '/cruises',
    priceLabel: 'Priced rate',
    geoLabel: 'City',
    showImage: false,
    ic: 'C',
    icBg: '#cffafe',
    icFg: '#0e7490',
  },
  visas: {
    label: 'Visas',
    route: '/visas',
    priceLabel: 'Default tier',
    geoLabel: 'Country',
    showImage: false,
    ic: 'V',
    icBg: '#dcfce7',
    icFg: '#15803d',
  },
  insurance: {
    label: 'Insurance',
    route: '/insurance',
    priceLabel: 'Default tier',
    geoLabel: 'Scope',
    showImage: false,
    ic: 'I',
    icBg: '#fae8ff',
    icFg: '#a21caf',
  },
};

export const isActive = (r: ProductCompletenessRow): boolean => r.status === 'ACTIVE';
/** Whether this product type needs an image to be quotable (activities only). */
export const imageRequired = (type: ProductType): boolean => TYPE_META[type].showImage;
/**
 * Ready to quote = a live priced rate and a geography to surface under; for
 * types that carry an image into the itinerary (activities), a missing image
 * also makes it not ready.
 */
export const isReady = (r: ProductCompletenessRow, type: ProductType): boolean =>
  r.priced && r.hasGeo && (!imageRequired(type) || r.hasImage);
/** Skipped = an active long-haul product (Europe/JP/KR/LK) that is not ready. */
export const isSkipped = (r: ProductCompletenessRow, type: ProductType): boolean =>
  isActive(r) && !isReady(r, type) && r.isLongHaul;
/** Blocked = active, not ready, and short-haul (counts as a real gap). */
export const isBlocked = (r: ProductCompletenessRow, type: ProductType): boolean =>
  isActive(r) && !isReady(r, type) && !r.isLongHaul;

export interface TypeStats {
  total: number;
  active: number;
  draft: number;
  ready: number;
  blocked: number;
  skipped: number;
  unapproved: number;
  noImage: number;
}
export const statsOf = (rows: ProductCompletenessRow[], type: ProductType): TypeStats => {
  const s: TypeStats = {
    total: 0,
    active: 0,
    draft: 0,
    ready: 0,
    blocked: 0,
    skipped: 0,
    unapproved: 0,
    noImage: 0,
  };
  for (const r of rows) {
    s.total += 1;
    if (r.status === 'DRAFT') s.draft += 1;
    if (!isActive(r)) continue;
    s.active += 1;
    if (isReady(r, type)) s.ready += 1;
    else if (r.isLongHaul) s.skipped += 1;
    else s.blocked += 1;
    if (!r.approved) s.unapproved += 1;
    if (!r.hasImage) s.noImage += 1;
  }
  return s;
};

export const fmt = (n: number): string => n.toLocaleString('en-IN');
